import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, ShoppingCart, Heart, Package, Fish } from 'lucide-react';
import { articulosService } from '@/api/articulos';
import { clientesService } from '@/api/clientes';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ProductCard from '@/components/common/ProductCard';
import CartPanel from '@/components/common/CartPanel';
import AppHeader from '@/components/layout/AppHeader';
import { useCartStore } from '@/store/cartStore';
import { useEmpresaStore } from '@/store/empresaStore';
import type { Articulo } from '@/types';
import styles from '@/styles/ui.module.css';

const FAMILIAS = [
  { key: 'salmon', nombre: 'Salmón', icon: Fish },
  { key: 'bacalao', nombre: 'Bacalao', icon: Fish },
  { key: 'pescados', nombre: 'Pescados', icon: Fish },
  { key: 'conservas', nombre: 'Conservas', icon: Package },
  { key: 'otros', nombre: 'Otros ahumados', icon: Package },
] as const;

type FamiliaKey = (typeof FAMILIAS)[number]['key'];

const getFamiliaKey = (codigo: string): FamiliaKey => {
  const normalizedCode = codigo.toUpperCase();

  if (normalizedCode.charAt(1) === 'Z') return 'conservas';
  if (normalizedCode.startsWith('S')) return 'salmon';
  if (normalizedCode.startsWith('B')) return 'bacalao';
  if (normalizedCode.startsWith('P')) return 'pescados';

  return 'otros';
};

const shouldHideArticulo = (articulo: Articulo): boolean => {
  const normalizedName = articulo.nombre.toLowerCase();

  return (
    normalizedName.includes('rougie') ||
    normalizedName.includes('selecta') ||
    normalizedName.includes('obrador')
  );
};

export default function CatalogoPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFamiliaKey, setSelectedFamiliaKey] = useState<string | undefined>();
  const [showCart, setShowCart] = useState(false);
  const { addItem, items, clienteId } = useCartStore();
  const empresaSeleccionada = useEmpresaStore((state) => state.empresaSeleccionada);

  const { data: clienteData } = useQuery({
    queryKey: ['cliente-detalle', clienteId],
    queryFn: () => clientesService.getClienteById(clienteId!),
    enabled: !!clienteId,
  });

  const { data: historialData } = useQuery({
    queryKey: ['cliente-historial-2y', clienteId],
    queryFn: () => articulosService.getHistorialCliente(Number(clienteId), 2, empresaSeleccionada?.id),
    enabled: !!clienteId && !!empresaSeleccionada,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['articulos', searchQuery, empresaSeleccionada?.id],
    queryFn: () =>
      articulosService.getArticulos({
        search: searchQuery,
        page: 1,
        limit: 500,
        empresa: empresaSeleccionada?.id,
      }),
  });

  const cartCount = items.reduce((sum, item) => sum + item.cantidad, 0);

  const productosFavoritosIds = useMemo(() => {
    if (!historialData) return new Set<number>();
    return new Set(historialData.map((h) => h.articuloId));
  }, [historialData]);

  const { productosFavoritos, productosRegulares } = useMemo(() => {
    if (!data?.data) return { productosFavoritos: [], productosRegulares: [], productosFiltrados: [] };

    let filtered = data.data.filter((art) => !shouldHideArticulo(art));
    if (selectedFamiliaKey) {
      filtered = filtered.filter((art) => getFamiliaKey(art.codigo) === selectedFamiliaKey);
    }

    const favoritos = filtered.filter((art) => productosFavoritosIds.has(art.id));
    const regulares = filtered.filter((art) => !productosFavoritosIds.has(art.id));

    return { productosFavoritos: favoritos, productosRegulares: regulares, productosFiltrados: filtered };
  }, [data?.data, productosFavoritosIds, selectedFamiliaKey]);

  const productCountByFamilia = useMemo(() => {
    if (!data?.data) return {};
    const counts: Record<string, number> = {};
    data.data
      .filter((art) => !shouldHideArticulo(art))
      .forEach((art) => {
      const familiaKey = getFamiliaKey(art.codigo);
      counts[familiaKey] = (counts[familiaKey] || 0) + 1;
      });
    return counts;
  }, [data?.data]);

  const handleAddProduct = (articulo: Articulo, cantidad: number) => {
    if (cantidad > 0) {
      addItem(articulo, cantidad, articulo.descuentoMax > 0 ? Math.min(5, articulo.descuentoMax) : 0);
    } else {
      const currentItem = items.find((item) => item.articulo.id === articulo.id);
      if (currentItem && currentItem.cantidad > 1) {
        addItem(articulo, cantidad, articulo.descuentoMax > 0 ? Math.min(5, articulo.descuentoMax) : 0);
      }
    }
  };

  const getQuantityInCart = (articuloId: number) =>
    items.find((item) => item.articulo.id === articuloId)?.cantidad || 0;

  return (
    <>
      <div className={styles.page}>
        <AppHeader title="Catalogo en linea" subtitle="Encuentra los productos y prepara el pedido" />

        <div className={`${styles.screenWrapper} ${styles.catalogScreenWrapper}`}>
          <section className={styles.section}>
            <div className={styles.card}>
              <div className={styles.columnGap}>
                <div className={styles.columnGap}>
                  <div className={styles.infoRow}>
                    <Package className="h-4 w-4" />
                    <span className={styles.mutedText}>
                      {empresaSeleccionada ? `Empresa ${empresaSeleccionada.id}` : 'Carpier Ahumados'}
                    </span>
                  </div>
                  <p className={styles.sectionTitle}>Pedido para {clienteData?.nombre || 'Cliente'}</p>
                  <p className={styles.mutedText}>Encuentra los productos que necesitas y añádelos al carrito</p>
                </div>
              </div>
            </div>
          </section>

          {!selectedFamiliaKey && productosFavoritos.length > 0 && (
            <section className={styles.section}>
              <div className="mb-3 flex items-center gap-2 px-1">
                <Heart className="h-5 w-5 text-pink-500" />
                <h3 className="text-lg font-bold text-gray-800">Los mas pedidos</h3>
                <span className="text-xs text-gray-500">(Top 10 de los ultimos 2 anos)</span>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                {productosFavoritos.slice(0, 10).map((articulo) => (
                  <ProductCard
                    key={articulo.id}
                    articulo={articulo}
                    onAdd={(cantidad: number) => handleAddProduct(articulo, cantidad)}
                    isFrequent={true}
                    quantityInCart={getQuantityInCart(articulo.id)}
                    variant="compact"
                  />
                ))}
              </div>
            </section>
          )}

          <section className={styles.section}>
            <div className={styles.card}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por codigo o nombre..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full rounded-lg border-2 border-gray-200 bg-white py-3 pl-10 pr-4 text-gray-900 outline-none transition-colors focus:border-primary-blue"
                />
              </div>
            </div>
          </section>

          <section className={styles.section}>
            <div className="mb-2 flex items-center gap-2 px-1">
              <span className="text-sm font-semibold text-gray-700">Filtrar por familia</span>
            </div>
            <div className="scrollbar-none flex gap-2 overflow-x-auto px-1 pb-2">
              <button
                onClick={() => setSelectedFamiliaKey(undefined)}
                className={`rounded-full px-4 py-2 text-sm font-semibold whitespace-nowrap transition-all ${
                  !selectedFamiliaKey
                    ? 'bg-[#27648d] text-white shadow-md'
                    : 'bg-white text-gray-700 shadow-sm ring-1 ring-gray-200 hover:shadow-md'
                }`}
                type="button"
              >
                Todas
                {!selectedFamiliaKey && data && (
                  <span className="ml-2 rounded-full bg-white/20 px-2 py-0.5 text-xs">{data.data.length}</span>
                )}
              </button>
              {FAMILIAS.map((familia) => {
                const Icon = familia.icon;
                const count = productCountByFamilia[familia.key] || 0;
                return (
                  <button
                    key={familia.key}
                    onClick={() => setSelectedFamiliaKey(familia.key)}
                    className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold whitespace-nowrap transition-all ${
                      selectedFamiliaKey === familia.key
                        ? 'border-[#27648d] bg-[#27648d] text-white shadow-md'
                        : 'border-[#dbe1ea] bg-white text-gray-700 shadow-sm hover:shadow-md'
                    }`}
                    type="button"
                  >
                    <Icon className="h-4 w-4" />
                    <span>{familia.nombre}</span>
                    {count > 0 && (
                      <span
                        className={`ml-1 rounded-full px-2 py-0.5 text-xs ${
                          selectedFamiliaKey === familia.key ? 'bg-white/20' : 'bg-gray-100'
                        }`}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </section>

          {isLoading ? (
            <section className={styles.section}>
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            </section>
          ) : productosRegulares.length > 0 ? (
            <section className={styles.section}>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {productosRegulares.map((articulo) => (
                  <ProductCard
                    key={articulo.id}
                    articulo={articulo}
                    onAdd={(cantidad: number) => handleAddProduct(articulo, cantidad)}
                    isFrequent={false}
                    quantityInCart={getQuantityInCart(articulo.id)}
                    variant="full"
                  />
                ))}
              </div>
            </section>
          ) : (
            <section className={styles.section}>
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Package className="mb-4 h-24 w-24 text-gray-400" />
                <h3 className="mb-2 text-lg font-semibold text-gray-700">No se encontraron productos</h3>
                <p className="text-sm text-gray-500">Intenta ajustar los filtros o la busqueda</p>
              </div>
            </section>
          )}
        </div>
      </div>

      <div className={styles.catalogCartFooter}>
        <div className={styles.catalogCartFooterInner}>
          <div className={styles.catalogCartFooterSummary}>
            <span className={styles.catalogCartFooterLabel}>Carrito</span>
            <strong className={styles.catalogCartFooterCount}>
              {cartCount} {cartCount === 1 ? 'articulo' : 'articulos'}
            </strong>
          </div>
          <button
            onClick={() => setShowCart(true)}
            className={styles.catalogCartFooterButton}
            type="button"
            aria-label="Abrir carrito"
          >
            <span className={styles.catalogCartFooterButtonIcon}>
              <ShoppingCart className="h-5 w-5" />
              {cartCount > 0 && <span className={styles.catalogCartFooterBadge}>{cartCount}</span>}
            </span>
            Ver carrito
          </button>
        </div>
      </div>

      <CartPanel isOpen={showCart} onClose={() => setShowCart(false)} />
    </>
  );
}
